import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AddressProblem, BuilderApi, DomainProblem } from "../api/contract";
import { RequestTimeoutError, requestTimeout } from "../lib/requestTimeout";

export interface UsePublishingOptions {
  api: BuilderApi;
  siteId: string;
  /** Read only while the publish review is open. */
  enabled: boolean;
  /** Back from checkout: keep checking until the payment provider confirms the plan. */
  awaitingPlan: boolean;
  /** How long to keep checking after checkout before asking the owner to check again. */
  planWaitMs?: number;
}

/**
 * The web address and whether the owner's plan covers publishing, plus choosing the address and
 * starting checkout. The server decides entitlement; this only reports it.
 */
export function usePublishing({ api, siteId, enabled, awaitingPlan, planWaitMs = 90_000 }: UsePublishingOptions) {
  const [waitedOut, setWaitedOut] = useState(false);
  const started = useRef<number | null>(null);
  const query = useQuery({
    queryKey: ["builder", "publishing", siteId],
    queryFn: ({ signal }) => requestTimeout(next => api.getPublishing(siteId, next), signal),
    enabled,
    staleTime: 0,
    retry: (count, error) => !(error instanceof RequestTimeoutError) && count < 1,
    refetchInterval: (current) => {
      if (!awaitingPlan || current.state.data?.entitled || waitedOut) return false;
      return 2_000;
    },
  });

  useEffect(() => {
    if (!awaitingPlan) {
      started.current = null;
      setWaitedOut(false);
      return;
    }
    started.current ??= Date.now();
    const timer = setTimeout(() => setWaitedOut(true), planWaitMs);
    return () => clearTimeout(timer);
  }, [awaitingPlan, planWaitMs]);

  const [addressProblem, setAddressProblem] = useState<AddressProblem | "failed" | null>(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const addressPending = useRef(false);
  const changeAddress = useCallback(async (subdomain: string): Promise<boolean> => {
    if (addressPending.current) return false;
    addressPending.current = true;
    setSavingAddress(true);
    setAddressProblem(null);
    try {
      const result = await requestTimeout(() => api.changeAddress(siteId, subdomain), undefined, 60_000);
      if ("problem" in result) {
        setAddressProblem(result.problem);
        return false;
      }
      await query.refetch();
      return true;
    } catch {
      setAddressProblem("failed");
      return false;
    } finally {
      addressPending.current = false;
      setSavingAddress(false);
    }
  }, [api, siteId, query]);

  const [checkout, setCheckout] = useState<"idle" | "starting" | "failed">("idle");
  const checkoutPending = useRef(false);
  const subscribe = useCallback(async (cycle: "monthly" | "annual") => {
    if (checkoutPending.current) return;
    checkoutPending.current = true;
    setCheckout("starting");
    try {
      window.location.assign(await requestTimeout(() => api.startCheckout(siteId, cycle), undefined, 60_000));
    } catch {
      checkoutPending.current = false;
      setCheckout("failed");
    }
  }, [api, siteId]);

  // Custom domain: connect, check, switch the address to it (or back), disconnect.
  const [domainBusy, setDomainBusy] = useState<"adding" | "checking" | "switching" | "removing" | null>(null);
  const [domainProblem, setDomainProblem] = useState<DomainProblem | null>(null);
  const domainPending = useRef(false);
  const domainAction = useCallback(async (kind: NonNullable<typeof domainBusy>, run: () => Promise<object>): Promise<boolean> => {
    if (domainPending.current) return false;
    domainPending.current = true;
    setDomainBusy(kind);
    setDomainProblem(null);
    try {
      const result = await requestTimeout(run, undefined, 60_000);
      if ("problem" in result) {
        setDomainProblem(result.problem as DomainProblem);
        return false;
      }
      await query.refetch();
      return true;
    } catch {
      setDomainProblem("failed");
      return false;
    } finally {
      domainPending.current = false;
      setDomainBusy(null);
    }
  }, [query]);

  return {
    status: query.data,
    domainBusy, domainProblem,
    addDomain: (hostname: string) => domainAction("adding", () => api.addDomain(siteId, hostname)),
    checkDomain: () => domainAction("checking", () => api.checkDomain(siteId)),
    switchAddress: (to: "domain" | "platform") => domainAction("switching", () => api.switchAddress(siteId, to)),
    removeDomain: () => domainAction("removing", () => api.removeDomain(siteId)),
    failed: Boolean(query.error) && !query.isFetching,
    /** Reads the web address and plan again, e.g. after the site went offline. */
    reload: async () => { await query.refetch(); },
    retry: () => {
      setWaitedOut(false);
      void query.refetch();
    },
    waitedOut,
    changeAddress, savingAddress, addressProblem, clearAddressProblem: () => setAddressProblem(null),
    subscribe, checkout,
  };
}

export type PublishingController = ReturnType<typeof usePublishing>;
